import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Award } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { nobishiroQuestions, nobishiroAbilities } from '../data/nobishiro-9-questions.js';
import { nobishiroAbilitiesData } from '../data/nobishiroAbilitiesData.js';
import { logEvent } from "firebase/analytics";
import NicknameSetupModal from '../components/common/NicknameSetupModal';
import { getAnalyticsInstance } from "../firebase";

const NobishiroDiagnosis = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isFinished, setIsFinished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [nicknameSet, setNicknameSet] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const analytics = getAnalyticsInstance();

  const totalQuestions = nobishiroQuestions.length;
  const currentQuestion = nobishiroQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswer = (answerValue) => {
    const newAnswers = { ...answers, [currentQuestion.id]: answerValue };
    setAnswers(newAnswers);
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      calculateAndSaveResults(newAnswers);
      setIsFinished(true);
      setShowNicknameModal(true);
    }
  };

  const calculateAndSaveResults = async (finalAnswers) => {
    if (!user) return;
    setIsSaving(true);
    
    const scores = {};
    Object.keys(nobishiroAbilities).forEach(key => { 
      scores[key] = { total: 0, count: 0 }; 
    });
    
    nobishiroQuestions.forEach(q => {
      const answer = finalAnswers[q.id];
      if (answer === undefined) return;
      const score = q.isReversed ? 6 - answer : answer;
      if (scores[q.category]) { 
        scores[q.category].total += score; 
        scores[q.category].count += 1; 
      }
    });
    
    const finalScores = {};
    Object.entries(scores).forEach(([key, value]) => { 
      finalScores[key] = value.count > 0 ? (value.total / value.count) : 0; 
    });
    
    const updatedTrizMastery = {};
    nobishiroAbilitiesData.forEach(item => {
      const itemScore = finalScores[item.key] || 0;
      item.relatedLaws.forEach(lawId => { 
        updatedTrizMastery[lawId] = { score: parseFloat(itemScore.toFixed(2)) }; 
      });
    });

    // 診断スコアからneurodiversityProfileを自動生成
    const toValue = (key) => Math.round((finalScores[key] || 0) / 5 * 100);
    const neurodiversityProfile = {
      processingStyle: {
        value: toValue('B'), // 高いほど全体的処理寄り
        description: toValue('B') > 60 ? '全体像から物事を捉える戦略的思考が得意です' :
                     toValue('B') < 40 ? '一つ一つ着実に積み上げる緻密な思考が得意です' :
                     'バランスの取れた情報処理スタイルです'
      },
      thinkingStyle: {
        value: toValue('I'), // 高いほど発散的思考寄り
        description: toValue('I') > 60 ? '既成概念にとらわれない自由な発想が強みです' :
                     toValue('I') < 40 ? '本質を見抜き最適解を導く収束的思考に長けています' :
                     '状況に応じて思考スタイルを切り替えられます'
      },
      attentionPattern: {
        value: toValue('H'), // 高いほど変動的注意寄り
        description: toValue('H') > 60 ? '広い興味関心を持ちマルチタスクで力を発揮します' :
                     toValue('H') < 40 ? '一つのタスクに深く集中し質の高い成果を生み出せます' :
                     '集中と切り替えをバランスよく行えます'
      }
    };

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        trizLawsMastery: updatedTrizMastery,
        neurodiversityProfile,
        nobishiroScores: finalScores,
        lastDiagnosisDate: new Date().toISOString(),
      }, { merge: true });

      if (analytics) { logEvent(analytics, 'diagnosis_complete', { diagnosis_type: 'nobishiro_9' }); }

    } catch (error) { 
      console.error('データベースの更新に失敗しました:', error);
    } finally { 
      setIsSaving(false); 
    }
  };

  const goToDashboard = () => navigate('/dashboard');

  if (showNicknameModal && !nicknameSet) {
    return (
      <NicknameSetupModal
        onComplete={(nick) => {
          setNicknameSet(true);
          setShowNicknameModal(false);
        }}
      />
    );
  }

  if (isFinished) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl mx-auto my-12 text-center">
        <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900">診断完了！</h1>
        <p className="text-gray-600 mt-2 mb-8">お疲れ様でした。診断結果はダッシュボードに反映されました。</p>
        <button 
          onClick={goToDashboard} 
          disabled={isSaving} 
          className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-blue-300"
        >
          {isSaving ? '結果を保存中...' : 'ダッシュボードで結果を見る'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 max-w-3xl mx-auto my-12">
      <div className="text-center mb-6">
        <Brain className="w-12 h-12 text-blue-600 mx-auto mb-2" />
        <h1 className="text-2xl font-bold text-gray-900">NOBISHIROコンピテンシー® 能力診断</h1>
        <p className="text-gray-500 mt-1">あなたの能力を可視化します</p>
      </div>
      
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>進捗</span>
          <span>質問 {currentQuestionIndex + 1} / {totalQuestions}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      
      <div className="text-center bg-gray-50 p-6 rounded-lg min-h-[120px] flex items-center justify-center border">
        <p className="text-lg font-semibold text-gray-800">
          {currentQuestion?.text || '質問を読み込んでいます...'}
        </p>
      </div>
      
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-2 text-center text-sm">
        {[
          { l: '全く当てはまらない', v: 1, c: 'red' },
          { l: 'あまり当てはまらない', v: 2, c: 'orange' },
          { l: 'どちらでもない', v: 3, c: 'gray' },
          { l: 'やや当てはまる', v: 4, c: 'blue' },
          { l: '非常によく当てはまる', v: 5, c: 'green' }
        ].map(opt => (
          <button 
            key={opt.v} 
            onClick={() => handleAnswer(opt.v)} 
            className={`p-3 rounded-lg bg-${opt.c}-100 text-${opt.c}-800 hover:bg-${opt.c}-200 transition`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
};

export default NobishiroDiagnosis;